import { supabase } from '@/integrations/supabase/client';

export interface PerformanceMetric {
  metricName: string;
  metricValue: number;
  metricUnit?: string;
  pageUrl: string;
  sessionId: string;
  additionalData?: Record<string, any>;
}

class PerformanceMonitor {
  private sessionId: string;
  private metricsQueue: PerformanceMetric[] = [];
  private flushInterval: number = 30000; // 30 seconds
  private flushTimer?: number;

  constructor() {
    this.sessionId = this.generateSessionId();
    this.initWebVitals();
    this.initCustomMarkers();
    this.startFlushTimer();
  }

  private generateSessionId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  private async initWebVitals() {
    // Import web-vitals dynamically
    try {
      const webVitals = await import('web-vitals');
      
      // Largest Contentful Paint
      webVitals.onLCP((metric) => {
        this.trackMetric({
          metricName: 'LCP',
          metricValue: metric.value,
          metricUnit: 'ms',
          pageUrl: window.location.pathname,
          sessionId: this.sessionId,
          additionalData: { rating: metric.rating },
        });
      });

      // Note: FID was replaced by INP in web-vitals v3+

      // Cumulative Layout Shift
      webVitals.onCLS((metric) => {
        this.trackMetric({
          metricName: 'CLS',
          metricValue: metric.value,
          metricUnit: 'score',
          pageUrl: window.location.pathname,
          sessionId: this.sessionId,
          additionalData: { rating: metric.rating },
        });
      });

      // Time to First Byte
      webVitals.onTTFB((metric) => {
        this.trackMetric({
          metricName: 'TTFB',
          metricValue: metric.value,
          metricUnit: 'ms',
          pageUrl: window.location.pathname,
          sessionId: this.sessionId,
          additionalData: { rating: metric.rating },
        });
      });

      // First Contentful Paint
      webVitals.onFCP((metric) => {
        this.trackMetric({
          metricName: 'FCP',
          metricValue: metric.value,
          metricUnit: 'ms',
          pageUrl: window.location.pathname,
          sessionId: this.sessionId,
          additionalData: { rating: metric.rating },
        });
      });

      // Interaction to Next Paint
      webVitals.onINP((metric) => {
        this.trackMetric({
          metricName: 'INP',
          metricValue: metric.value,
          metricUnit: 'ms',
          pageUrl: window.location.pathname,
          sessionId: this.sessionId,
          additionalData: { rating: metric.rating },
        });
      });
    } catch (error) {
      console.error('Failed to initialize web-vitals:', error);
    }
  }

  private initCustomMarkers() {
    // Track navigation timing
    if (window.performance && window.performance.timing) {
      window.addEventListener('load', () => {
        const timing = window.performance.timing;
        const loadTime = timing.loadEventEnd - timing.navigationStart;
        const domContentLoaded = timing.domContentLoadedEventEnd - timing.navigationStart;
        const domInteractive = timing.domInteractive - timing.navigationStart;

        this.trackMetric({
          metricName: 'PageLoadTime',
          metricValue: loadTime,
          metricUnit: 'ms',
          pageUrl: window.location.pathname,
          sessionId: this.sessionId,
        });

        this.trackMetric({
          metricName: 'DOMContentLoaded',
          metricValue: domContentLoaded,
          metricUnit: 'ms',
          pageUrl: window.location.pathname,
          sessionId: this.sessionId,
        });

        this.trackMetric({
          metricName: 'DOMInteractive',
          metricValue: domInteractive,
          metricUnit: 'ms',
          pageUrl: window.location.pathname,
          sessionId: this.sessionId,
        });
      });
    }

    // Track resource timing
    if (window.performance && window.performance.getEntriesByType) {
      window.addEventListener('load', () => {
        const resources = window.performance.getEntriesByType('resource');
        const totalResourceSize = resources.reduce((sum, r: any) => sum + (r.transferSize || 0), 0);
        const totalResourceTime = resources.reduce((sum, r: any) => sum + (r.duration || 0), 0);

        this.trackMetric({
          metricName: 'TotalResourceSize',
          metricValue: totalResourceSize,
          metricUnit: 'bytes',
          pageUrl: window.location.pathname,
          sessionId: this.sessionId,
          additionalData: { resourceCount: resources.length },
        });

        this.trackMetric({
          metricName: 'TotalResourceLoadTime',
          metricValue: totalResourceTime,
          metricUnit: 'ms',
          pageUrl: window.location.pathname,
          sessionId: this.sessionId,
          additionalData: { resourceCount: resources.length },
        });
      });
    }
  }

  private startFlushTimer() {
    this.flushTimer = window.setInterval(() => {
      this.flush();
    }, this.flushInterval);
  }

  public trackMetric(metric: PerformanceMetric) {
    this.metricsQueue.push(metric);
    
    // Auto-flush if queue is large
    if (this.metricsQueue.length >= 20) {
      this.flush();
    }
  }

  public async flush() {
    if (this.metricsQueue.length === 0) return;

    const metrics = [...this.metricsQueue];
    this.metricsQueue = [];

    try {
      const { data: { user } } = await supabase.auth.getUser();
      const { data: profile } = await supabase
        .from('profiles')
        .select('tenant_id')
        .eq('user_id', user?.id)
        .single();

      if (!user || !profile?.tenant_id) {
        console.warn('Cannot flush metrics: user or tenant not found');
        return;
      }

      const enrichedMetrics = metrics.map(metric => ({
        user_id: user.id,
        tenant_id: profile.tenant_id,
        session_id: metric.sessionId,
        metric_name: metric.metricName,
        metric_value: metric.metricValue,
        metric_unit: metric.metricUnit || null,
        page_url: metric.pageUrl,
        user_agent: navigator.userAgent,
        connection_type: (navigator as any).connection?.effectiveType || null,
        device_memory: (navigator as any).deviceMemory || null,
        is_offline: !navigator.onLine,
        additional_data: metric.additionalData || null,
        reported_at: new Date().toISOString(),
      }));

      const { error } = await supabase
        .from('performance_metrics')
        .insert(enrichedMetrics);

      if (error) {
        console.error('Failed to flush performance metrics:', error);
        // Re-queue metrics for retry
        this.metricsQueue.unshift(...metrics);
      }
    } catch (error) {
      console.error('Error flushing performance metrics:', error);
      // Re-queue metrics for retry
      this.metricsQueue.unshift(...metrics);
    }
  }

  public destroy() {
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
    }
    this.flush(); // Final flush
  }
}

// Singleton instance
let performanceMonitor: PerformanceMonitor | null = null;

export function initPerformanceMonitoring() {
  if (!performanceMonitor) {
    performanceMonitor = new PerformanceMonitor();
  }
  return performanceMonitor;
}

export function getPerformanceMonitor() {
  return performanceMonitor;
}

export function destroyPerformanceMonitoring() {
  if (performanceMonitor) {
    performanceMonitor.destroy();
    performanceMonitor = null;
  }
}
